import { isEmpty } from "lodash";
import {
  fireCartAuditEvent,
  checkAndFireLongDuration,
  updateCartAuditSession,
  setCartCreatedAt,
  getLastOrderPlacedMid,
  setLastOrderPlacedMid,
  logEvent,
  LOG_LEVELS,
  CART_AUDIT_EVENTS,
} from "../../../../../helpers/cartAuditHelper";
import { SaleItem } from "../../../../../models/saleItem";
import { SaleDataType } from "../../../../../models/sale";

/**
 * WEB-ONLY module — `redux-store/middleware/saleUpdate/cartEventEffect/cartEventHandler`.
 *
 * There is no counterpart on the other platform: its whole `saleUpdate` middleware is a single
 * file with no cart-audit telemetry at all (verified with `ls`, per rule 4). So this folder has
 * one variant rather than three — the same shape as the other one-sided modules in this tree.
 * Its only importers are its sibling effects, which are one-sided for the same reason.
 *
 * ⚠️ `logEvent` here is the CART-AUDIT logger from `cartAuditHelper`, NOT the effects bridge's
 * Sentry-breadcrumb `logEvent`. Same name, different function — do not "simplify" the import.
 */

/**
 * Handles CART_CREATED and CART_EMPTIED cart audit events.
 * Returns true if caller should stop further processing
 * (when prevSaleId !== curSaleId).
 */
export function handleCartCreatedOrEmptied(
  prevSaleId: string,
  curSaleId: string,
  previousCartItems: SaleItem[],
  currentCartItems: SaleItem[],
): void {
  // CART_CREATED — first item added to empty cart
  if (previousCartItems.length === 0 && currentCartItems.length > 0) {
    setCartCreatedAt(Date.now());
    setLastOrderPlacedMid(null); // Reset stale flag on new cart
    fireCartAuditEvent({ eventName: CART_AUDIT_EVENTS.CART_CREATED, cartId: curSaleId, cartValue: 0 });
    updateCartAuditSession(curSaleId, 0, true);
  }

  // CART_EMPTIED — all items removed
  if (previousCartItems.length > 0 && currentCartItems.length === 0 && prevSaleId === curSaleId && getLastOrderPlacedMid() !== curSaleId) {
    const cartValue = previousCartItems.reduce(
      (sum, item) => sum + (item.itemSubTotal || 0),
      0
    );
    checkAndFireLongDuration(curSaleId, cartValue);
    fireCartAuditEvent({ eventName: CART_AUDIT_EVENTS.CART_EMPTIED, cartId: curSaleId, cartValue });
    setCartCreatedAt(null);
    updateCartAuditSession(curSaleId, 0, false);
  }
}

/**
 * Keeps the cart audit session in sync with the current cart value.
 * Called from resetSaleEffect on every saveSaleData dispatch.
 *
 * Internally checks:
 *  - action type is saveSaleData
 *  - current cart has items (no point updating session for empty cart)
 */
export function handleCartSessionUpdate(currentSale: SaleDataType) {
  if (!(currentSale?.saleItems?.length > 0)) return;

  updateCartAuditSession(
    currentSale?.mid,
    currentSale?.billTotal || 0,
    true
  );
}

/**
 * Handles CART_DELETED cart audit event.
 *
 * Internally checks all conditions:
 *  - action type is saveSaleData
 *  - current saleItems empty
 *  - previous saleItems had items
 *  - sale was not validated (i.e. not a successful save)
 *  - mid changed (truly a cart-to-new-sale transition)
 *  - this mid was NOT just placed as an order (suppress false CART_DELETED)
 */
export function handleCartDeleted(prevSale: SaleDataType, currentSale: SaleDataType, saleValidated: boolean) {

  // Condition check — was this an actual cart deletion?
  if (!isCartDeletionScenario(prevSale, currentSale, saleValidated )) return;


  const prevMid = prevSale?.mid;
  const newMid = currentSale?.mid;

  // Skip if mid hasn't changed (not actually a new cart transition)
  if (prevMid === newMid) return;

  // Suppress false CART_DELETED if this mid was just placed as an order
  const lastOrderMid = getLastOrderPlacedMid();
  if (lastOrderMid && lastOrderMid === prevMid) {
    logEvent(LOG_LEVELS.INFO, `Skipping ${CART_AUDIT_EVENTS.CART_DELETED} => mid ${prevMid} was just placed as order`);
    setLastOrderPlacedMid(null); // Clear flag after use
    return;
  }

  // Genuine cart deletion — fire events
  checkAndFireLongDuration(prevMid, prevSale?.billTotal || 0);
  fireCartAuditEvent({
    eventName: CART_AUDIT_EVENTS.CART_DELETED,
    cartId: prevMid,
    cartValue: prevSale?.billTotal || 0,
    channelOverride: {
      channelId: prevSale?.channelId || '',
      channelDefId: prevSale?.channelDefId || '',
      channelName: prevSale?.channelName || '',
    },
  });
}

/**
 * Returns true if the action represents an external discount cleanup case.
 * Used by resetSaleEffect to decide whether to run XENO cleanup.
 *
 * This is the same condition as handleCartDeleted, exposed as a utility
 * so resetSaleEffect can run discount cleanup without duplicating logic.
 */
export function isCartDeletionScenario(
  prevSale: SaleDataType,
  currentSale: SaleDataType,
  saleValidated: boolean,
): boolean {
  return (
    isEmpty(currentSale?.saleItems) &&
    !isEmpty(prevSale?.saleItems) &&
    !saleValidated
  );
}
